import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Grid, LinearProgress, Button } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { isEmpty, noop } from "lodash";
import { useHistory, useLocation } from "react-router-dom";

import { PermissionedDisplay, SectionGridContainer } from ".";
import useColumnSelection from "../../hooks/useColumnSelection";
import Search from "../FormElements/Search";
import Table, { RowType, ColumnType, SortDirType } from "./Table";

const useStyles = makeStyles((theme) => ({
  buttonBackground: { background: theme.palette.primary.red },
}));

const IndexTable = ({
  columns,
  rows,
  totalRowsCount,
  defaultRowsPerPage,
  fetchResource,
  searchProps,
  createPerms,
  createPath,
  createButtonText,
  rowsPerPageOptions,
  searchable,
  muiTableProps,
  fetchResourceDeps,
  defaultSortBy,
  defaultSortDir,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const location = useLocation();
  const history = useHistory();
  const { onSelectColumn, sortDir, sortBy } = useColumnSelection({
    columns: columns.filter(({ sortable }) => sortable).map((col) => col.canonicalName),
    sortBy: defaultSortBy,
    sortDir: defaultSortDir,
  });

  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchText, setSearchText] = useState("");

  const fetch = async () => {
    setLoading(true);
    await fetchResource({
      search: searchText,
      ordering: sortBy && (sortDir === "asc" ? sortBy : `-${sortBy}`),
      page_size: rowsPerPage,
      page: pageNumber + 1,
    });
    setLoading(false);
  };

  useEffect(() => {
    setPageNumber(0);
    fetch();
  }, [sortBy, sortDir, rowsPerPage]);

  useEffect(() => {
    fetch();
  }, [pageNumber, searchText, ...fetchResourceDeps]);

  return (
    <SectionGridContainer spaceContents>
      <Grid container spacing={2}>
        <Grid item container xs={12} justify="space-between">
          {(searchable || !isEmpty(searchProps)) && (
            <Grid item xs={12} md={6} lg={4}>
              <Search
                fullWidth
                onChange={setSearchText}
                debounce={500}
                loading={loading}
                {...searchProps}
              />
            </Grid>
          )}
          {createPath && (
            <Grid item container justify="flex-end" xs={12} md={6}>
              <PermissionedDisplay permission={createPerms}>
                <Button
                  className={classes.buttonBackground}
                  onClick={() => history.push(createPath, { background: location })}
                >
                  {createButtonText}
                </Button>
              </PermissionedDisplay>
            </Grid>
          )}
        </Grid>
      </Grid>
      {loading && (
        <Grid item xs={12} style={{ padding: theme.spacing(2) }}>
          <LinearProgress color="secondary" data-testid="loading-bar" />
        </Grid>
      )}
      <Table
        muiTableProps={muiTableProps}
        rows={rows}
        columns={columns}
        paginationProps={{
          rowsPerPageOptions,
          totalRowsCount,
          rowsPerPage,
          setPageNumber,
          setRowsPerPage,
          pageNumber,
        }}
        sortProps={{
          sortBy,
          sortDir,
          onSelectColumn,
        }}
      />
    </SectionGridContainer>
  );
};

IndexTable.propTypes = {
  columns: PropTypes.arrayOf(ColumnType).isRequired,
  rows: PropTypes.arrayOf(RowType).isRequired,
  fetchResource: PropTypes.func,
  fetchResourceDeps: PropTypes.array,
  totalRowsCount: PropTypes.number,
  defaultRowsPerPage: PropTypes.number,
  searchProps: PropTypes.object,
  createPerms: PropTypes.arrayOf(PropTypes.string),
  createPath: PropTypes.string,
  createButtonText: PropTypes.string,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  searchable: PropTypes.bool,
  muiTableProps: PropTypes.object,
  defaultSortBy: PropTypes.string,
  defaultSortDir: SortDirType,
};

IndexTable.defaultProps = {
  fetchResource: noop,
  fetchResourceDeps: [],
  totalRowsCount: null,
  defaultRowsPerPage: 100,
  searchProps: {},
  createPerms: [],
  createButtonText: "",
  createPath: "",
  rowsPerPageOptions: [5, 10, 50, 100],
  searchable: false,
  muiTableProps: {},
  defaultSortBy: "",
  defaultSortDir: "asc",
};

export default IndexTable;
